import React from 'react';
import { Link } from 'react-router-dom';
import { 
  FaCheck, 
  FaStar, 
  FaUser, 
  FaGraduationCap, 
  FaHeadset, 
  FaCalendarAlt, 
  FaCertificate, 
  FaUsers, 
  FaBook, 
  FaVideo, 
  FaMicrophone, 
  FaGlobe, 
  FaClock, 
  FaShieldAlt,
  FaRocket,
  FaHeart,
  FaLightbulb,
  FaTrophy,
  FaHandshake,
  FaCompass
} from 'react-icons/fa';

const PremiumBenefits: React.FC = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6 text-white">
              Unlock Your Full Farsi Potential
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-indigo-100 max-w-3xl mx-auto">
              Experience the most comprehensive Persian learning platform with premium features designed to accelerate your language journey
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/subscription" 
                className="bg-white text-indigo-600 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-indigo-50 transition-all duration-200 flex items-center justify-center"
              >
                <FaRocket className="mr-2" />
                Start Free Trial
              </Link>
              <Link 
                to="/pricing" 
                className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-white hover:text-indigo-600 transition-all duration-200 flex items-center justify-center"
              >
                <FaStar className="mr-2" />
                View Pricing
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Premium Features Grid */}
      <div className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Premium Features That Transform Learning
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From personalized tutoring to cultural immersion, discover how premium membership accelerates your Persian language mastery
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* 1-on-1 Tutoring */}
            <div className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100">
              <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mb-6">
                <FaUser className="text-indigo-600 text-2xl" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">1-on-1 Tutoring Sessions</h3>
              <p className="text-gray-600 mb-6">
                Connect with native Persian speakers for personalized learning sessions. Get real-time feedback, pronunciation practice, and cultural insights.
              </p>
              <ul className="space-y-2">
                <li className="flex items-center text-sm text-gray-600">
                  <FaCheck className="text-green-500 mr-2" />
                  Premium: 2 sessions/month
                </li>
                <li className="flex items-center text-sm text-gray-600">
                  <FaCheck className="text-green-500 mr-2" />
                  Platinum: Unlimited sessions
                </li>
                <li className="flex items-center text-sm text-gray-600">
                  <FaCheck className="text-green-500 mr-2" />
                  Flexible scheduling
                </li>
              </ul>
            </div>

            {/* Advanced Courses */}
            <div className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mb-6">
                <FaGraduationCap className="text-purple-600 text-2xl" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Advanced & Specialized Courses</h3>
              <p className="text-gray-600 mb-6">
                Access exclusive courses covering business Persian, literary analysis, poetry interpretation, and advanced grammar structures.
              </p>
              <ul className="space-y-2">
                <li className="flex items-center text-sm text-gray-600">
                  <FaCheck className="text-green-500 mr-2" />
                  Business Persian
                </li>
                <li className="flex items-center text-sm text-gray-600">
                  <FaCheck className="text-green-500 mr-2" />
                  Classical Literature
                </li>
                <li className="flex items-center text-sm text-gray-600">
                  <FaCheck className="text-green-500 mr-2" />
                  Advanced Grammar
                </li>
              </ul>
            </div>

            {/* Cultural Events */}
            <div className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-6">
                <FaCalendarAlt className="text-green-600 text-2xl" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Cultural Events & Webinars</h3>
              <p className="text-gray-600 mb-6">
                Immerse yourself in Persian culture through exclusive virtual events, cooking classes, poetry readings, and cultural workshops.
              </p>
              <ul className="space-y-2">
                <li className="flex items-center text-sm text-gray-600">
                  <FaCheck className="text-green-500 mr-2" />
                  Monthly cultural webinars
                </li>
                <li className="flex items-center text-sm text-gray-600">
                  <FaCheck className="text-green-500 mr-2" />
                  Cooking classes
                </li>
                <li className="flex items-center text-sm text-gray-600">
                  <FaCheck className="text-green-500 mr-2" />
                  Poetry readings
                </li>
              </ul>
            </div>

            {/* Personalized Learning */}
            <div className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100">
              <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mb-6">
                <FaCompass className="text-yellow-600 text-2xl" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Personalized Learning Plans</h3>
              <p className="text-gray-600 mb-6">
                Get a customized learning roadmap based on your goals, current level, and preferred learning style. Track progress with detailed analytics.
              </p>
              <ul className="space-y-2">
                <li className="flex items-center text-sm text-gray-600">
                  <FaCheck className="text-green-500 mr-2" />
                  Custom learning path
                </li>
                <li className="flex items-center text-sm text-gray-600">
                  <FaCheck className="text-green-500 mr-2" />
                  Progress tracking
                </li>
                <li className="flex items-center text-sm text-gray-600">
                  <FaCheck className="text-green-500 mr-2" />
                  Adaptive content
                </li>
              </ul>
            </div>

            {/* Priority Support */}
            <div className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-6">
                <FaHeadset className="text-red-600 text-2xl" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Priority Support</h3>
              <p className="text-gray-600 mb-6">
                Get fast, personalized support from our expert team. Platinum members receive direct access to the Vajak development team.
              </p>
              <ul className="space-y-2">
                <li className="flex items-center text-sm text-gray-600">
                  <FaCheck className="text-green-500 mr-2" />
                  24/7 email support
                </li>
                <li className="flex items-center text-sm text-gray-600">
                  <FaCheck className="text-green-500 mr-2" />
                  Live chat (Platinum)
                </li>
                <li className="flex items-center text-sm text-gray-600">
                  <FaCheck className="text-green-500 mr-2" />
                  Direct team access
                </li>
              </ul>
            </div>

            {/* Certificate */}
            <div className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-6">
                <FaCertificate className="text-blue-600 text-2xl" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Certificate of Completion</h3>
              <p className="text-gray-600 mb-6">
                Earn official certificates upon completing courses and pathways. Perfect for professional development and academic recognition.
              </p>
              <ul className="space-y-2">
                <li className="flex items-center text-sm text-gray-600">
                  <FaCheck className="text-green-500 mr-2" />
                  Course completion certificates
                </li>
                <li className="flex items-center text-sm text-gray-600">
                  <FaCheck className="text-green-500 mr-2" />
                  Pathway completion diplomas
                </li>
                <li className="flex items-center text-sm text-gray-600">
                  <FaCheck className="text-green-500 mr-2" />
                  Professional recognition
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Comparison Section */}
      <div className="bg-gray-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Choose Your Learning Journey
            </h2>
            <p className="text-xl text-gray-600">
              Compare plans and find the perfect fit for your Persian learning goals
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Free Plan */}
            <div className="bg-white p-8 rounded-xl shadow-lg border-2 border-gray-200">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Free</h3>
              <p className="text-gray-600 mb-6">Perfect for getting started</p>
              <div className="mb-6">
                <span className="text-4xl font-bold text-gray-900">$0</span>
                <span className="text-gray-600">/month</span>
              </div>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center">
                  <FaCheck className="text-green-500 mr-3" />
                  <span>Basic courses</span>
                </li>
                <li className="flex items-center">
                  <FaCheck className="text-green-500 mr-3" />
                  <span>Community forum access</span>
                </li>
                <li className="flex items-center">
                  <FaCheck className="text-green-500 mr-3" />
                  <span>Limited practice exercises</span>
                </li>
                <li className="flex items-center text-gray-400">
                  <FaCheck className="mr-3" />
                  <span>No tutoring sessions</span>
                </li>
                <li className="flex items-center text-gray-400">
                  <FaCheck className="mr-3" />
                  <span>No advanced courses</span>
                </li>
              </ul>
              <Link to="/register" className="w-full bg-gray-100 text-gray-800 py-3 px-4 rounded-lg font-medium hover:bg-gray-200 transition-all duration-200 block text-center">
                Get Started Free
              </Link>
            </div>

            {/* Premium Plan */}
            <div className="bg-white p-8 rounded-xl shadow-lg border-2 border-indigo-600 relative">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-indigo-600 text-white px-6 py-2 rounded-full text-sm font-semibold">
                MOST POPULAR
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Premium</h3>
              <p className="text-gray-600 mb-6">For serious learners</p>
              <div className="mb-6">
                <span className="text-4xl font-bold text-gray-900">$9.99</span>
                <span className="text-gray-600">/month</span>
              </div>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center">
                  <FaCheck className="text-green-500 mr-3" />
                  <span>Everything in Free</span>
                </li>
                <li className="flex items-center">
                  <FaCheck className="text-green-500 mr-3" />
                  <span>Advanced courses</span>
                </li>
                <li className="flex items-center">
                  <FaCheck className="text-green-500 mr-3" />
                  <span>2 tutoring sessions/month</span>
                </li>
                <li className="flex items-center">
                  <FaCheck className="text-green-500 mr-3" />
                  <span>Personalized learning plan</span>
                </li>
                <li className="flex items-center">
                  <FaCheck className="text-green-500 mr-3" />
                  <span>Priority support</span>
                </li>
                <li className="flex items-center text-gray-400">
                  <FaCheck className="mr-3" />
                  <span>No cultural events</span>
                </li>
              </ul>
              <Link to="/subscription" className="w-full bg-indigo-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-indigo-700 transition-all duration-200 block text-center">
                Choose Premium
              </Link>
            </div>

            {/* Platinum Plan */}
            <div className="bg-gradient-to-br from-gray-900 to-gray-800 p-8 rounded-xl shadow-lg text-white">
              <h3 className="text-2xl font-bold text-white mb-2">Platinum</h3>
              <p className="text-gray-300 mb-6">Ultimate experience</p>
              <div className="mb-6">
                <span className="text-4xl font-bold text-white">$24.99</span>
                <span className="text-gray-300">/month</span>
              </div>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center">
                  <FaCheck className="text-green-400 mr-3" />
                  <span>Everything in Premium</span>
                </li>
                <li className="flex items-center">
                  <FaCheck className="text-green-400 mr-3" />
                  <span>Unlimited tutoring sessions</span>
                </li>
                <li className="flex items-center">
                  <FaCheck className="text-green-400 mr-3" />
                  <span>1-on-1 coaching</span>
                </li>
                <li className="flex items-center">
                  <FaCheck className="text-green-400 mr-3" />
                  <span>Cultural events & webinars</span>
                </li>
                <li className="flex items-center">
                  <FaCheck className="text-green-400 mr-3" />
                  <span>Certificate of completion</span>
                </li>
                <li className="flex items-center">
                  <FaCheck className="text-green-400 mr-3" />
                  <span>Direct Vajak team support</span>
                </li>
              </ul>
              <Link to="/subscription" className="w-full bg-white text-gray-900 py-3 px-4 rounded-lg font-medium hover:bg-gray-100 transition-all duration-200 block text-center">
                Go Platinum
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Testimonials Section */}
      <div className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Success Stories from Premium Members
            </h2>
            <p className="text-xl text-gray-600">
              See how premium features have transformed the learning experience for our members
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-xl shadow-lg">
              <div className="flex items-center mb-6">
                <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center">
                  <FaUser className="text-indigo-600 text-2xl" />
                </div>
                <div className="ml-4">
                  <h4 className="font-bold text-lg">Sarah Mitchell</h4>
                  <p className="text-gray-600">Premium Member</p>
                  <div className="flex text-yellow-400 mt-1">
                    <FaStar /><FaStar /><FaStar /><FaStar /><FaStar />
                  </div>
                </div>
              </div>
              <p className="text-gray-700 italic">
                "The 1-on-1 tutoring sessions completely changed my learning. My tutor helped me understand the nuances of Persian that I couldn't get from books alone. I'm now confident speaking with native speakers!"
              </p>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-lg">
              <div className="flex items-center mb-6">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center">
                  <FaUser className="text-purple-600 text-2xl" />
                </div>
                <div className="ml-4">
                  <h4 className="font-bold text-lg">Amir Khosravi</h4>
                  <p className="text-gray-600">Platinum Member</p>
                  <div className="flex text-yellow-400 mt-1">
                    <FaStar /><FaStar /><FaStar /><FaStar /><FaStar />
                  </div>
                </div>
              </div>
              <p className="text-gray-700 italic">
                "As a heritage speaker, I wanted to deepen my understanding of Persian literature. The cultural events and advanced courses have given me insights into my culture that I never had before."
              </p>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-lg">
              <div className="flex items-center mb-6">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                  <FaUser className="text-green-600 text-2xl" />
                </div>
                <div className="ml-4">
                  <h4 className="font-bold text-lg">Elena Rodriguez</h4>
                  <p className="text-gray-600">Premium Member</p>
                  <div className="flex text-yellow-400 mt-1">
                    <FaStar /><FaStar /><FaStar /><FaStar /><FaStar />
                  </div>
                </div>
              </div>
              <p className="text-gray-700 italic">
                "The personalized learning plan helped me focus on business Persian, which I needed for work. Within 3 months, I was able to conduct meetings in Persian. The ROI was incredible!"
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-800 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6">
            Ready to Accelerate Your Persian Learning?
          </h2>
          <p className="text-xl mb-8 text-indigo-100 max-w-3xl mx-auto">
            Join thousands of learners who have transformed their Persian skills with our premium features. Start your 7-day free trial today!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/subscription" 
              className="bg-white text-indigo-600 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-indigo-50 transition-all duration-200 flex items-center justify-center"
            >
              <FaRocket className="mr-2" />
              Start Free Trial
            </Link>
            <Link 
              to="/pricing" 
              className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-white hover:text-indigo-600 transition-all duration-200 flex items-center justify-center"
            >
              <FaStar className="mr-2" />
              View All Plans
            </Link>
          </div>
          <p className="text-sm text-indigo-200 mt-6">
            No credit card required • Cancel anytime • 7-day free trial
          </p>
        </div>
      </div>
    </div>
  );
};

export default PremiumBenefits; 